// Contratos públicos de la página de detalle de evento. Refleja la
// proyección que la API local (apps/api) hace de la respuesta del
// Core (Baila Pro) sobre dos endpoints encadenados:
//
//   1) GET /events/url/{slug}/stores/{storeId}     → metadatos
//   2) POST /events/{eventId}/sessions/list        → sesiones
//
// El backend resuelve los dos en cascada y devuelve un único shape
// `EventDetailResponse` con ambos.

import { z } from 'zod';
import { PaginatedResponseSchema } from './pagination.js';

// Zona por defecto del público de eventos mientras el Core no incluya
// la zona horaria del recinto en el contrato.
export const DEFAULT_EVENT_TIME_ZONE = 'Europe/Madrid';

const ExternalPurchaseUrlSchema = z
  .string()
  .url()
  .max(2048)
  .refine((value) => /^https?:\/\//i.test(value));

/**
 * Sesión proyectada al cliente. Subconjunto estable de lo que entrega
 * el Core: lo justo para pintar la fila + componer el href de checkout
 * (`customShortLink` o `originalShortLink`).
 */
export const EventSessionPublicSchema = z.object({
  id: z.string(),
  displayName: z.string().optional(),
  startDateTime: z.string().optional(),
  presaleStartDate: z.string().max(100).optional(),
  presaleEndDate: z.string().max(100).optional(),
  sessionDurationMinutes: z.number().int().nonnegative().optional(),
  doorsOpeningMinutes: z.number().int().nonnegative().optional(),
  hasAvailability: z.boolean().optional(),
  // Estado de la sesión según el Core (string: "OnSale", "SoldOut",
  // …). La UI mapea sólo los códigos que necesita pintar.
  status: z.string().optional(),
  minPrice: z.number().nonnegative().optional(),
  maxPrice: z.number().nonnegative().optional(),
  currency: z.string().optional(),
  promo: z
    .object({
      minPrice: z.number().nonnegative().optional(),
      startDate: z.string().max(100).optional(),
      endDate: z.string().max(100).optional(),
      displayName: z.string().max(200).optional(),
      type: z.string().max(50).optional(),
      discount: z.number().nonnegative().optional(),
    })
    .optional(),
  customShortLink: z.string().optional(),
  originalShortLink: z.string().optional(),
  // URL de compra externa (eventos `Mode=External`, p. ej. onebox). Si
  // viene, el CTA de compra debe ir aquí en vez de a tickets.baila.fm.
  externalPurchaseUrl: ExternalPurchaseUrlSchema.optional(),
});

export const EventSessionsResponseSchema = PaginatedResponseSchema(
  EventSessionPublicSchema,
);

/** Abono de evento proyectado por la API a partir de passConfigurations/list. */
export const EventPassPublicSchema = z.object({
  id: z.string().min(1).max(200),
  displayName: z.string().min(1).max(200).optional(),
  availabilityStatus: z.string().max(50).optional(),
  startDate: z.string().max(100).optional(),
  endDate: z.string().max(100).optional(),
  sessionIds: z.array(z.string().min(1).max(200)).max(100).optional(),
  minPrice: z.number().nonnegative().optional(),
  maxPrice: z.number().nonnegative().optional(),
  currency: z.string().optional(),
});

// Imagen proyectada al cliente: URL ya compuesta (baseUrl + path),
// listo para `<Image src=...>`.
export const EventImageSchema = z.object({
  url: z.string(),
  alt: z.string().optional(),
});

export const EventLineUpItemSchema = z.object({
  id: z.string().optional(),
  // Slug del artista (para enlazar a su ficha por slug). El line-up del
  // Core no lo trae; se enriquece en la API desde la lista de artistas.
  slug: z.string().optional(),
  displayName: z.string(),
  imageUrl: z.string().optional(),
});

/**
 * Evento proyectado al cliente para la página de detalle. Mantiene
 * sólo los campos que la UI consume; el resto del shape del Core no
 * sale de `apps/api`.
 */
export const EventDetailPublicSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),

  // Imagen principal (la del hero). Convención: priorizamos
  // CoverImage > SquareImage > PosterImage según lo que entregue el
  // Core.
  imageUrl: z.string().optional(),
  coverImageUrl: z.string().optional(),
  squareImageUrl: z.string().optional(),
  posterImageUrl: z.string().optional(),

  // Venue
  venueId: z.string().optional(),
  venueDisplayName: z.string().optional(),
  venueLocation: z.string().optional(),
  venueCity: z.string().optional(),
  venueCountry: z.string().optional(),
  venueAddress: z.string().optional(),
  venueCoordinates: z
    .object({ lat: z.number(), lng: z.number() })
    .optional(),

  // Organización
  organizationId: z.string(),
  organizationDisplayName: z.string().optional(),

  // Categorías
  categoryId: z.string().optional(),
  categoryDisplayName: z.string().optional(),
  subcategories: z.array(z.string()).optional(),

  // Flags y modos del Core
  type: z.number().int().optional(),
  isSingleSession: z.boolean().optional(),
  isQueueActive: z.boolean().optional(),
  status: z.string().optional(),
  // Estado del evento en la tienda del Core (p. ej. Cancelled/Finished).
  storeStatus: z.string().optional(),
  mode: z.string().optional(),

  // Evento "archivado": ficha servida desde el fallback local (eventos
  // antiguos del CMS viejo que el Core ya no sirve). Se muestra el
  // contenido pero NO se pueden comprar entradas (`ticketsUnavailable`).
  isArchived: z.boolean().optional(),
  ticketsUnavailable: z.boolean().optional(),

  // Slugs para hrefs de checkout. Algunos endpoints del Core devuelven
  // `ShortLink` (= slug del evento); el `customShortLink` queda
  // reservado para cuando el Core lo entregue diferenciado.
  customShortLink: z.string().optional(),
  originalShortLink: z.string().optional(),

  // URL de compra externa a nivel evento (eventos `Mode=External` de
  // sesión única; en multi-sesión la URL va por sesión).
  externalPurchaseUrl: ExternalPurchaseUrlSchema.optional(),

  // Texto
  description: z.string().optional(),
  importantInfo: z.string().optional(),

  // Tiempos
  shouldShowDates: z.boolean().optional(),
  startDateTime: z.string().optional(),
  endDateTime: z.string().optional(),
  doorsOpenAt: z.string().optional(),
  // Minutos antes del inicio. El Core lo entrega como TimeSpan y la API
  // lo valida antes de exponerlo a la web.
  doorsOpenOffsetMinutes: z.number().int().nonnegative().optional(),
  durationMinutes: z.number().int().nonnegative().optional(),

  // Specs adicionales
  ageRecommendation: z.string().optional(),
  languages: z.array(z.string()).optional(),
  accessibilityDescription: z.string().optional(),

  // Rango de precios (banner)
  priceRange: z
    .object({
      min: z.number().nonnegative(),
      max: z.number().nonnegative(),
      currency: z.string(),
    })
    .optional(),

  // Galería y line up
  gallery: z.array(EventImageSchema).optional(),
  lineUp: z.array(EventLineUpItemSchema).optional(),
});

export const EventDetailResponseSchema = z.object({
  event: EventDetailPublicSchema,
  sessions: EventSessionsResponseSchema,
  passes: z.array(EventPassPublicSchema).default([]),
});

export type EventSessionPublic = z.infer<typeof EventSessionPublicSchema>;
export type EventSessionsResponse = z.infer<typeof EventSessionsResponseSchema>;
export type EventPassPublic = z.infer<typeof EventPassPublicSchema>;
export type EventDetailPublic = z.infer<typeof EventDetailPublicSchema>;
export type EventDetailResponse = z.infer<typeof EventDetailResponseSchema>;
export type EventImage = z.infer<typeof EventImageSchema>;
export type EventLineUpItem = z.infer<typeof EventLineUpItemSchema>;
